import React, { useEffect, useState } from 'react'
import { PageHeader, Section } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { useIsMounted } from '@vend/react-utilities'
import { trackError } from '@vend/utilities'
import { reactular } from '@vendhq/reactular'
import find from 'lodash/find'

import {
  useCashMovementsManager,
  useRegisterManager,
  useUserManager,
  useVdFeatureManager,
} from '../../ng-compat/hooks'
import { useAppSelector } from '../../state/hooks'
import { NoPermissionsPage } from '../react/react-utilities/NoPermissionsPage'
import {
  selectActiveRegisterId,
  selectLoggedInUserId,
} from '../../state/slices/posState/posStateSlice'

import {
  CASH_MOVEMENT_TYPES,
  FEATURE_ENABLE_REGISTER_ACCESS_CONTROL,
  PERMISSIONS,
} from '../../constants'
import { OpenRegisterPanel } from '../register/OpenRegisterPanel'
import {
  CashManageActionBar,
  CashManageActionBarWithoutButtons,
} from './CashManageActionBar'
import { CashManagementDisabled } from './CashManagementDisabled'
import { FailedCashMovementsBanner } from './FailedCashMovementsBanner'
import { SetRegisterCashFloat } from './SetRegisterCashFloat'
import { CashMovementsList } from './list/CashMovementsList'
import { Movement } from './utils/types'

const CashManagementView: React.FC<{}> = () => {
  const t = useT()
  const isMountedRef = useIsMounted()
  const registerManager = useRegisterManager()
  const cashMovementsManager = useCashMovementsManager()
  const isActiveRegisterOpen = registerManager.isActiveRegisterOpen()
  const cashManagementEnabled = cashMovementsManager.isCashManagementEnabled()

  // Get active registerId from posState in redux store
  const activeRegisterId = useAppSelector(selectActiveRegisterId)
  // Trigger a re-render when the user changes
  useAppSelector(selectLoggedInUserId)

  const [movements, setMovements] = useState<Movement[]>([])
  const [isCashFloatSet, setIsCashFloatSet] = useState<boolean>(false)
  const [
    failedCashMovementsRetrieval,
    setFailedCashMovementsRetrieval,
  ] = useState<boolean>(false)
  const [
    waitingForInitialCashMovements,
    setWaitingForInitialCashMovements,
  ] = useState<boolean>(true)
  const [retrieveMovementsTimes, setRetrieveMovementsTimes] = useState<number>(
    0
  )
  const vdFeatureManager = useVdFeatureManager()
  const userManager = useUserManager()

  const hasCashManagementAccess = () => {
    const registerAccessControlEnabled = vdFeatureManager.get(
      FEATURE_ENABLE_REGISTER_ACCESS_CONTROL
    )
    return registerAccessControlEnabled
      ? userManager.hasPermission(PERMISSIONS.CASH_IN_OUT)
      : true
  }

  const onRecordMovmentSuccess = (cashMovement: Movement) => {
    setMovements([cashMovement, ...movements])
  }

  // This event is handled when the cash register is being opened for the first
  // time
  const handleInitialOpenDrawer = (cashMovement: Movement) => {
    if (cashMovement) {
      setMovements([cashMovement, ...movements])
      setIsCashFloatSet(true)
    }
  }

  useEffect(() => {
    if (cashManagementEnabled && isActiveRegisterOpen) {
      setWaitingForInitialCashMovements(true)
      cashMovementsManager
        .getCashMovements()
        .then(cashMovements => {
          if (cashMovements.length > 0 && isMountedRef.current) {
            setFailedCashMovementsRetrieval(false)
            setIsCashFloatSet(
              Boolean(
                find(cashMovements, {
                  type: CASH_MOVEMENT_TYPES.OPENING_CASH_FLOAT,
                })
              )
            )
            setMovements(cashMovements)
          }
        })
        .catch(error => {
          setFailedCashMovementsRetrieval(true)
          trackError(
            '[CashManagementView] Error retrieving cash movements',
            error
          )
        })
        .finally(() => {
          if (isMountedRef.current) {
            setWaitingForInitialCashMovements(false)
          }
        })
    }
  }, [
    activeRegisterId,
    retrieveMovementsTimes,
    cashManagementEnabled,
    cashMovementsManager,
    isActiveRegisterOpen,
    isMountedRef,
  ])

  if (!activeRegisterId) {
    return null
  }

  if (!cashManagementEnabled) {
    return <CashManagementDisabled activeRegisterId={activeRegisterId} />
  }

  const content = () => {
    if (!hasCashManagementAccess()) {
      return (
        <>
          <CashManageActionBarWithoutButtons />
          <Section className="vd-pa10">
            <NoPermissionsPage
              copy={t(
                "You don't have permission to record cash movements. Switch to a user with permission."
              )}
            />
          </Section>
        </>
      )
    }

    if (!isActiveRegisterOpen) {
      return (
        <Section>
          <OpenRegisterPanel />
        </Section>
      )
    }

    const displayRegisterCashFloat =
      !waitingForInitialCashMovements &&
      !failedCashMovementsRetrieval &&
      !isCashFloatSet

    return (
      <>
        {failedCashMovementsRetrieval && (
          <FailedCashMovementsBanner
            onRetry={() =>
              setRetrieveMovementsTimes(retrieveMovementsTimes + 1)
            }
          />
        )}
        {displayRegisterCashFloat && (
          <SetRegisterCashFloat onSuccess={handleInitialOpenDrawer} />
        )}
        {!waitingForInitialCashMovements && isCashFloatSet && (
          <>
            <CashManageActionBar
              onRecordMovementSuccess={onRecordMovmentSuccess}
            />
            <Section>
              <CashMovementsList movements={movements} />
            </Section>
          </>
        )}
      </>
    )
  }

  return (
    <>
      <PageHeader>
        {t('Cash Management', { _context: 'Page Header' })}
      </PageHeader>
      {content()}
    </>
  )
}

export const CashManagementViewComponent = reactular(
  CashManagementView,
  [],
  'reactWrapper'
)
